import { makeAutoObservable, runInAction } from 'mobx';
import { TranscriptEditorCore } from './TranscriptEditorCore';
import { TranscriptContainer } from './models/TranscriptContainer';
import { Segment } from './models/Segment';
import { 
  TranscriptEditorChangeType,
  TranscriptVoiceMapping,
} from './types';
import { RootStore } from '../stores-v2/RootStore';
import { SelectionService } from './services/SelectionService';
import { SpeakerService } from './services/SpeakerService';
import { AudioService } from './services/AudioService';
import { TimelineService } from './services/TimelineService';
import { TranscriptVersion } from '../repositories/TranscriptRepository';
import WaveformData from 'waveform-data';
import { TranscriptData } from '@src/types.ts/transcript';
import { TranscriptElement } from './models/TranscriptElement';
import { TranscriptEditorObserver } from './types';
import { SpeakerEvent } from './services/interfaces';
import { Speaker } from '@src/components/content-components/SpeakerSelector';
import VideoCreatorStore from '@src/stores/VideoCreatorStore';
import { toJS } from 'mobx';


/**
 * Interface for transcript version URLs
 */
export interface TranscriptVersionUrls {
  [TranscriptVersion.LATEST]: string;
  [TranscriptVersion.ORIGINAL]?: string;
  [TranscriptVersion.REV]?: string;
  [TranscriptVersion.AWS]?: string;
  [TranscriptVersion.ASSEMBLY]?: string;
  [TranscriptVersion.AWS_REV]?: string;
}

/**
 * SPEAKER TERMINOLOGY IN TRANSCRIPT EDITOR
 * 
 * This code deals with two different types of speaker identifiers:
 * 
 * 1. Speaker Labels (speakerLabel): 
 *    - Anonymous transcript speaker identifiers (e.g., "spk_0", "spk_1")
 *    - ALWAYS start with "spk_" prefix
 *    - Used internally in the transcript data structures
 *    - Applied to segments in the transcript
 * 
 * 2. Speaker IDs (datoSpeakerId):
 *    - DatoCMS speaker identifiers (backend persistent IDs)
 *    - NEVER start with "spk_" prefix
 *    - Correspond to actual named speakers with metadata
 *    - Used for storage and integration with CMS
 * 
 * The mapping system (TranscriptVoiceMapping) connects speaker labels to speaker IDs:
 * {
 *   mappings: {
 *     "spk_0": "datoCmsId1",  // Maps anonymous label to actual speaker ID
 *     "spk_1": "datoCmsId2",
 *     ...
 *   }
 * }
 * 
 * All UI components should:
 * 1. Use speaker labels (spk_X) for transcript segment references
 * 2. Use the mapping system to convert between labels and IDs
 * 3. Get speaker names from DatoCMS speakers, not directly from labels
 */

/**
 * Interface for speaker data from DatoCMS
 */
interface SpeakerData {
  id: string;
  name: string;
  title?: string;
  bio?: string;
  voice?: string;
  color?: string;
}

/**
 * TranscriptEditorStore
 * 
 * MobX store that provides a reactive interface to the TranscriptEditorCore.
 * Handles UI state, user interactions, and manages connections to various services.
 * 
 * Auto-save Behavior:
 * - Commands executed through the TranscriptEditorCommandManager automatically trigger saving
 * - The CommandManager handles debounced auto-saving after operations with a 500ms delay
 * - Store methods that manipulate transcript data should use commands to ensure auto-save happens
 * - Direct calls to saveChanges() should only be used for manual save actions, not after operations
 */
export class TranscriptEditorStore {
  // Core editor state
  editor: TranscriptEditorCore;
  language: string;
  isLoading: boolean = false;
  error: string | null = null;
  transcriptionLoaded: boolean = false;
  waveformData: WaveformData | null = null;
  
  // Version tracking
  transcriptionVersionUrls: TranscriptVersionUrls = {
    [TranscriptVersion.LATEST]: '',
  };
  currentTranscriptVersion: TranscriptVersion = TranscriptVersion.LATEST;
  
  /**
   * MobX observable to track the container
   * We don't use a simple getter because we need the container to be observable
   * for MobX reactivity to work properly
   */
  private _container: TranscriptContainer | null = null;
  
  /**
   * Get the transcript container from the core editor
   * This is a computed property to ensure we always have the most up-to-date container
   */
  get container(): TranscriptContainer | null {
    return this._container;
  }
  
  // Track transcript loading state
  transcriptLoaded: boolean = false;

  // Speaker state
  storyId: string | null = null;
  albumId: string | null = null;
  storySpeakers: Array<SpeakerData> = [];
  albumSpeakers: Array<SpeakerData> = [];
  
  // Track which storyId was used to load speakers to prevent unnecessary reloads
  private loadedSpeakersForStoryId: string | null = null;
  
  // Track whether services have been created and initialized
  private servicesCreated: boolean = false;
  
  // Root store reference
  private rootStore: RootStore;
  
  /**
   * Gets the current user from UserIdentityStore
   */
  get currentUser() {
    return this.rootStore.userIdentityStore.currentIdentity;
  }

  /**
   * Gets the root store
   * @returns The root store instance
   */
  getRootStore(): RootStore {
    return this.rootStore;
  }
  
  /**
   * Gets the current user ID safely
   * @returns User ID or undefined if not available
   */
  get currentUserId(): string | undefined {
    return this.currentUser?.id;
  }
  
  /**
   * Creates a new TranscriptEditorStore
   * 
   * @param rootStore Root store reference containing user identity
   * @param storyId ID of the story to edit
   * @param albumId ID of the album containing the story
   * @param audioUrl URL of the audio file
   * @param waveformData Waveform data for visualization
   * @param transcription Transcript data
   * @param initialLanguage Initial language code (defaults to 'en-us')
   */
  constructor(
    rootStore: RootStore, 
    storyId: string, 
    albumId: string, 
    audioUrl: string, 
    waveformData: WaveformData | null, 
    transcription: TranscriptData, 
    initialLanguage: string = 'en-us'
  ) {
    if (!rootStore) {
      throw new Error('RootStore is required for TranscriptEditorStore');
    }
    
    this.rootStore = rootStore;
    this.language = initialLanguage;
    this.storyId = storyId;
    this.albumId = albumId;
    
    // Initialize the editor core
    this.editor = new TranscriptEditorCore(
      storyId, 
      albumId, 
      audioUrl, 
      waveformData, 
      transcription, 
      initialLanguage
    );
    
    this.waveformData = waveformData;
    
    // Initialize container reference
    this._container = this.editor.getContainer();
    
    // Create services
    this.createServices();
    
    // // Load speakers and mappings if we have a valid story ID
    // if (storyId && storyId !== '') {
    //   this.loadSpeakers();
    //   this.editor.loadSpeakerMappings();
    // }
    
    // Setup MobX for reactivity
    makeAutoObservable(this, {
      editor: true,
    });

    // Setup observers for editor changes
    this.setupEditorObservers();
    
    // Verify user identity is available
    if (!this.currentUser) {
      // User identity not available, but continue anyway
    }
  }

  /**
   * Checks if the editor is ready for interaction
   */
  isReady(): boolean {
    return !!this.storyId;
  }

  /**
   * Sets the active language for editing
   */
  setLanguage(language: string): void {
    this.language = language;
    this.editor.setLanguage(language);
  }

  /**
   * Gets the current language from the editor
   */
  getLanguage(): string {
    if (!this.editor) {
      return '';
    }
    return this.editor.getCurrentLanguage();
  }

  /**
   * Sets the story ID and refreshes speakers
   */
  setStoryId(storyId: string | null): void {
    // If the story ID is changing, reset the loaded speakers tracking
    if (this.storyId !== storyId) {
      this.loadedSpeakersForStoryId = null;
    }

    this.storyId = storyId;
    this.editor.setStoryId(storyId || '');
    // if (storyId && storyId !== '') {
    //   this.loadSpeakers();
    //   this.editor.loadSpeakerMappings();
    // }
  }

  /**
   * Gets the current story ID
   */
  getStoryId(): string | null {
    return this.storyId;
  }

  getLoadedSpeakersForStoryId(): string | null {
    return this.loadedSpeakersForStoryId;
  }
  
  /**
   * Sets the album ID
   */
  setAlbumId(albumId: string | null): void {
    this.albumId = albumId;
    this.editor.setAlbumId(albumId || '');
  }

  /**
   * Gets the current album ID
   */
  getAlbumId(): string | null {
    return this.albumId;
  } 

  /**
   * Resets speaker-related state for a new story
   */
  resetSpeakerState(): void {
    runInAction(() => {
      this.loadedSpeakersForStoryId = null;
      this.albumSpeakers = [];
      this.storySpeakers = [];
    });
  }

  /**
   * Sets the audio URL in the editor
   */
  setAudioUrl(audioUrl: string | null): void {
    this.editor.setAudioUrl(audioUrl || '');
  }
  
  /**
   * Sets transcript version URLs
   * @param urls Object with URLs for different transcript versions
   */
  setTranscriptionVersionUrls(urls: Partial<TranscriptVersionUrls>): void {
    runInAction(() => {
      this.transcriptionVersionUrls = {
        ...this.transcriptionVersionUrls,
        ...urls
      };
    });
  }

  /**
   * Set the current transcript version
   * @param version The transcript version
   */
  setCurrentTranscriptVersion(version: TranscriptVersion): void {
    runInAction(() => {
      this.currentTranscriptVersion = version;
    });
  }

  /**
   * Get available transcript versions
   * @returns Array of available versions
   */
  getAvailableVersions(): TranscriptVersion[] {
    return Object.entries(this.transcriptionVersionUrls)
      .filter(([_, url]) => !!url)
      .map(([version]) => version as TranscriptVersion);
  }
  
  /**
   * Checks if current version is not LATEST
   * Used to notify users that edits made to non-latest versions will be lost when saved
   * @returns true if not on LATEST version
   */
  isViewingNonLatestVersion(): boolean {
    return this.currentTranscriptVersion !== TranscriptVersion.LATEST;
  }
  
  /**
   * Load a specific version of the transcript
   * 
   * @param version The version to load
   * @returns Promise that resolves when the load is complete
   */
  async loadTranscriptVersion(version: TranscriptVersion): Promise<boolean> {
    try {
      runInAction(() => {
        this.isLoading = true;
        this.error = null;
      });
      
      // Ensure we have URLs for the transcript versions
      const versionUrl = this.transcriptionVersionUrls[version];
      if (!versionUrl) {
        console.error(`No URL available for transcript version: ${version}`);
        runInAction(() => {
          this.isLoading = false;
        });
        return false;
      }

      // Fetch the transcript data
      try {
        const response = await fetch(versionUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch transcript: ${response.statusText}`);
        }
        
        const transcriptData = await response.json();
        
        // Reset command stack first to prevent any pending changes
        if (this.editor.commandManager) {
          this.editor.commandManager.clearStacks();
        }
        
        // Set the new transcription data
        this.setTranscription(transcriptData);
        
        // Update the current version
        this.setCurrentTranscriptVersion(version);
        
        runInAction(() => {
          this.isLoading = false;
        });
        return true;
      } catch (error) {
        console.error(`Error loading transcript version ${version}:`, error);
        runInAction(() => {
          this.isLoading = false;
        });
        return false;
      }
    } catch (error) {
      console.error(`Error in loadTranscriptVersion:`, error);
      runInAction(() => {
        this.isLoading = false;
      });
      return false;
    }
  }

  /**
   * Gets the audio URL from the editor
   */
  getAudioUrl(): string | null {
    return this.editor.getAudioUrl();
  }

  /**
   * Sets the waveform data for audio visualization
   */
  setWaveformData(waveformData: WaveformData | null): void {
    this.waveformData = waveformData;
    this.editor.setWaveformData(waveformData);
  }

  /**
   * Gets the current waveform data
   */
  getWaveformData(): WaveformData | null {
    return this.waveformData;
  }
  
  /**
   * Sets the transcript data in the editor
   */
  setTranscription(transcription: TranscriptData | null): void {
    if (!transcription) {
      return;
    }
    
    // Make sure we have a valid elements array
    const validTranscription = {
      ...transcription,
      elements: transcription.elements || [],
      fullText: transcription.fullText || '',
      language: transcription.language || 'en-us',
      version: transcription.version || 0
    };
    
    // Set transcription in editor
    this.editor.setTranscription(validTranscription);
    
    // Update our observable container reference
    runInAction(() => {
      this._container = this.editor.getContainer();
    });
    
    this.transcriptionLoaded = true;
  }
  
  /**
   * Gets the transcript data from the editor
   */
  getTranscription(): TranscriptData | null {
    if (!this.editor) {
      return null;
    }
    return this.editor.getTranscription();
  }

  /**
   * Loads the original transcription from storage
   */
  loadOriginalTranscription(language?: string): void {
    if (!this.editor) {
      return;
    }
    this.editor.loadOriginalTranscription(language);
  }
  
  /**
   * Setup observers to ensure MobX reactivity for editor changes
   */
  private setupEditorObservers(): void {
    const mobxReactivityObserver: TranscriptEditorObserver = {
      onEditorChange: (
        editor: TranscriptEditorCore,
        changeType: TranscriptEditorChangeType,
        payload: Record<string, any>,
      ) => {
        // ---------------------------------------------------------------
        // UI refresh hook – any time the core emits a change this switch
        // statement determines whether we need to mutate observable state
        // which in turn causes React components wrapped with `observer()`
        // to re-render.
        // ---------------------------------------------------------------

        console.log('changeType', changeType);  
        switch (changeType) {
          case TranscriptEditorChangeType.TRANSCRIPT_LOADED:
            this.refreshState();
            // Explicitly set transcriptLoaded flag based on container state
            runInAction(() => {
              const container = this.editor.getContainer();
              this._container = container; // Update our observable container reference
              const hasTranscript = !!container && container.segments.length > 0;
              this.transcriptLoaded = hasTranscript;
            });
            break;
            
          case TranscriptEditorChangeType.LANGUAGE_CHANGED:
            runInAction(() => {
              this.language = editor.getCurrentLanguage();
            });
            break;
            
          case TranscriptEditorChangeType.ELEMENT_UPDATED:
          case TranscriptEditorChangeType.PARAGRAPH_SPLIT:
          case TranscriptEditorChangeType.PARAGRAPH_MERGED:
          case TranscriptEditorChangeType.PARAGRAPH_MOVED:
          case TranscriptEditorChangeType.SEGMENT_CREATED:
          case TranscriptEditorChangeType.SEGMENT_DELETED:
          case TranscriptEditorChangeType.BATCH_COMPLETED:
          case TranscriptEditorChangeType.TEXT_REPLACED_GLOBAL:
          case TranscriptEditorChangeType.SEGMENTS_MERGED:
          case TranscriptEditorChangeType.POST_HOOKS_APPLIED:
          case TranscriptEditorChangeType.TRANSCRIPT_CHANGED:
          case TranscriptEditorChangeType.TRANSCRIPT_MODIFIED:
          case TranscriptEditorChangeType.CHUNK_CHANGED:
          case TranscriptEditorChangeType.LANGUAGE_CHANGED:
          case TranscriptEditorChangeType.SPEAKER_ASSIGNED:
          case TranscriptEditorChangeType.SPEAKER_REMOVED:
          case TranscriptEditorChangeType.SPEAKER_MAPPINGS_LOADED:
          case TranscriptEditorChangeType.OPERATION_APPLIED:
          case TranscriptEditorChangeType.ELEMENT_UPDATED:
          case TranscriptEditorChangeType.ELEMENT_ADDED:
          case TranscriptEditorChangeType.ELEMENT_DELETED:
          case TranscriptEditorChangeType.PARAGRAPH_ELEMENTS_UPDATED:
          case TranscriptEditorChangeType.PARAGRAPH_DELETED:
          case TranscriptEditorChangeType.PARAGRAPH_SPLIT:
          case TranscriptEditorChangeType.PARAGRAPH_MERGED:
          case TranscriptEditorChangeType.PARAGRAPH_MOVED:
          case TranscriptEditorChangeType.SEGMENT_CREATED:
          case TranscriptEditorChangeType.SEGMENT_DELETED:
          case TranscriptEditorChangeType.SEGMENTS_MERGED:
          case TranscriptEditorChangeType.BATCH_COMPLETED:
          case TranscriptEditorChangeType.CHANGES_SAVED:
          case TranscriptEditorChangeType.CHANGES_DISCARDED:
          case TranscriptEditorChangeType.ERROR_OCCURRED:
          case TranscriptEditorChangeType.SELECTION_CHANGED:
          case TranscriptEditorChangeType.UNTIMED_TEXT_ADDED:
          case TranscriptEditorChangeType.UNTIMED_TEXT_PROCESSED:
          case TranscriptEditorChangeType.TEXT_REPLACED_GLOBAL:
          case TranscriptEditorChangeType.TEXT_REPLACED_SELECTION:
          case TranscriptEditorChangeType.POST_HOOKS_APPLIED:
            // Update our observable container reference when the transcript changes
            runInAction(() => {
              if (payload.container) {
                this._container = payload.container;
                this._container?.incrementCounter();
                console.log('container', this._container);
              // } else {
              //   console.log('using editor container');
              //   const container = this.editor.getContainer();
              //   this._container = container;
              }
            });
            break;
            
          // Lightweight notification for speaker assignments
          case TranscriptEditorChangeType.SPEAKER_ASSIGNED:
            // Update our observable container reference when the transcript changes
            runInAction(() => {
              this._container = this.editor.getContainer();
              // console.log('SPEAKER_ASSIGNED!!!', payload);
              this.editor?.speakerService?.notifyObservers(SpeakerEvent.SPEAKER_ASSIGNED, payload);
            });
            break;
            
          case TranscriptEditorChangeType.ACTIVE_SEGMENT_CHANGED:
            break;
            
          case TranscriptEditorChangeType.ERROR_OCCURRED:
            runInAction(() => {
              this.error = payload.error;
            });
            break;
            
          default:
            break;
        }
      },
    };
    
    this.editor.registerObserver(mobxReactivityObserver);
  }
  
  /**
   * Force refresh all state from the editor
   */
  refreshState(): void {
    runInAction(() => {
      this.language = this.editor.getCurrentLanguage();
      this.waveformData = this.editor.getWaveformData();
      this._container = this.editor.getContainer(); // Update the observable container reference
    });
  }
  
  /**
   * Creates and assigns a speaker to selected elements or a specific speaker label
   *
   * This method is the centralized entry point for speaker creation and assignment.
   * It handles:
   * 1. Creating a new speaker using the SpeakerRepository
   * 2. Adding the speaker to local collections for UI consistency
   * 3. Determining elements for speaker assignment (from optional params or SelectionService)
   * 4. Creating and executing the command with appropriate parameters
   *
   * @param name Speaker name
   * @param title Optional speaker title
   * @param bio Optional speaker bio
   * @param voice Optional speaker voice preference
   * @param skipAssignment Optional flag to skip assignment step
   * @param skipStoryAddition Optional flag to skip adding speaker to story
   * @param existingSpeakerLabel Optional existing speaker label to map (for mapping anonymous speakers)
   * @returns Promise resolving to true if successful
   */
  async createAndAssignSpeaker(
    name: string,
    title: string,
    bio?: string,
    voice?: string,
    skipAssignment?: boolean,
    skipStoryAddition?: boolean,
    existingSpeakerLabel?: string
  ): Promise<boolean> {

    if (!this.editor || !this.currentUserId) {
      return false;
    }

    // Get speaker service from root store
    const speakerService = this.editor.speakerService;

    if (!speakerService) {
      return false;
    }

    try {
      // Create the speaker via the service
      const newSpeaker = await speakerService.createSpeaker(name, title, bio, voice, skipStoryAddition);

      if (!newSpeaker) {
        return false;
      }

      // Add to local speaker collections
      runInAction(() => {
        this.albumSpeakers.push(newSpeaker);
        if (!skipStoryAddition) {
          this.storySpeakers.push(newSpeaker);
        }
      });

      // If skipAssignment is true, skip the assignment part and return true
      if (skipAssignment) {
        return true;
      }

      let speakerLabel = existingSpeakerLabel || null;

      // If no existingSpeakerLabel was provided and we're not in segment selection mode, generate a new label
      if (!speakerLabel && this.editor.selectionService && this.editor.selectionService.currentSelectionType !== 'segment') {
        // Generate a speaker label
        speakerLabel = `spk_${this.editor.nextSpeakerNumber()}`;
      }

      // If we have an existingSpeakerLabel but no selection, update the speaker mapping directly
      if (existingSpeakerLabel && (!this.editor.selectionService || !this.editor.selectionService.hasSelection)) {
        // Just update the mapping without needing a selection
        this.editor.updateSpeakerMapping(existingSpeakerLabel, newSpeaker.id);

        // Notify about the update - this refreshes the speakers panel
        this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
          speakerLabel: existingSpeakerLabel,
          datoSpeakerId: newSpeaker.id
        });

        // Record the operation
        this.editor.recordOperation({
          type: 'speaker_assignment',
          data: {
            speakerLabel: existingSpeakerLabel,
            datoSpeakerId: newSpeaker.id,
            scope: 'mapping_only'
          },
          userId: this.currentUserId,
          timestamp: Date.now()
        });

        return true;
      }

      // Assign speaker to current selection
      const result = await this.assignSpeakerToCurrentSelection(speakerLabel, newSpeaker.id);

      // Clear selection after assignment completes
      this.editor.selectionService?.clearAllSelections();

      return result;

    } catch (error) {
      return false;
    }
  }

  /**
   * Assign a speaker to the current selection (segment or elements)
   * This method delegates to the editor core to handle the assignment process
   *
   * If there's an active selection, it will:
   * - For segment selection: Assign the speaker to the entire segment
   * - For element selection: Assign the speaker to the selected elements only
   *
   * If no selection exists but a speakerLabel is provided, it will:
   * - Update the speaker mapping only, without changing any elements
   *
   * @param speakerLabel The transcript speaker label (e.g., "spk_0")
   * @param datoSpeakerId The DatoCMS speaker ID
   * @returns Promise resolving to true if successful
   */
  async assignSpeakerToCurrentSelection(speakerLabel: string | null, datoSpeakerId: string | null): Promise<boolean> {
    if (!this.editor) {
      console.warn('Cannot assign speaker: editor not initialized');
      return false;
    }

    // If no selection exists but we have both labels, just update the mapping
    if ((!this.editor.selectionService || !this.editor.selectionService.hasSelection) &&
        speakerLabel && datoSpeakerId) {
      // Update mapping only
      this.editor.updateSpeakerMapping(speakerLabel, datoSpeakerId);

      // Record the operation
      this.editor.recordOperation({
        type: 'speaker_assignment',
        data: {
          speakerLabel: speakerLabel,
          datoSpeakerId: datoSpeakerId,
          scope: 'mapping_only'
        },
        userId: this.currentUserId || '',
        timestamp: Date.now()
      });

      // Notify observers about the update
      this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
        speakerLabel: speakerLabel,
        datoSpeakerId: datoSpeakerId
      });

      return true;
    }

    // Otherwise, delegate to the editor core for selection-based assignment
    const success = await this.editor.assignSpeakerToCurrentSelection(
      speakerLabel,
      datoSpeakerId,
      this.currentUserId || ''
    );

    // For non-anonymous speakers, add reference to DatoCMS
    if (success && datoSpeakerId && this.storyId) {
      try {
        const speakerRepo = this.rootStore.datoClientStore.speakerRepository;
        await speakerRepo.addSpeakerReferenceToStory(datoSpeakerId, this.storyId);
        if (this.albumId) {
          await speakerRepo.addSpeakerReferenceToAlbum(datoSpeakerId, this.albumId);
        }
      } catch (error) {
        console.error('Error adding speaker reference to DatoCMS:', error);
      }
    }

    return success;
  }
  
  /**
   * Edit a transcript element's text
   * @param segmentId Segment containing the element
   * @param paragraphId Paragraph containing the element
   * @param elementId Element to update
   * @param newText New text value
   * @param newSpeakerId Optional speaker ID for the element
   * @returns Boolean indicating success
   */
  updateElementText(
    segmentId: string, 
    paragraphId: string, 
    elementId: string, 
    newText: string, 
    newSpeakerId?: string
  ): boolean {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot update element text: editor or user identity not available');
      return false;
    }
    
    return this.editor.updateElementText(
      segmentId, 
      paragraphId, 
      elementId, 
      newText, 
      this.currentUserId
    );
  }
  
  /**
   * Debounced version of updateElementText that batches rapid text changes
   * This is used during active typing to avoid creating a separate operation for each character
   */
  updateElementTextDebounced(
    segmentId: string,
    paragraphId: string,
    elementId: string,
    newText: string,
    userId: string,
    immediate: boolean = false
  ): boolean {
    if (!this.editor) {
      console.warn('Cannot update element text: editor not available');
      return false;
    }
    
    // Get the result from the editor
    // This uses the command pattern which will trigger auto-save via CommandManager
    const result = this.editor.updateElementTextDebounced(
      segmentId,
      paragraphId,
      elementId,
      newText,
      userId,
      immediate
    );
    
    return result;
  }
  
  /**
   * Update all elements in a paragraph
   */
  updateParagraphElements(
    segmentId: string, 
    paragraphId: string, 
    newElements: TranscriptElement[]
  ): boolean {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot update paragraph elements: editor or user identity not available');
      return false;
    }
    
    // Start batch operation
    this.editor.beginBatch();
    
    try {
      // Call editor method to update paragraph elements
      // This uses the command pattern which will trigger auto-save via CommandManager
      const success = this.editor.updateParagraphElements(
        segmentId,
        paragraphId,
        newElements,
        this.currentUserId
      );
      
      return success;
    } finally {
      // End batch operation
      this.editor.endBatch();
    }
  }  
  
  /**
   * Save all pending changes
   * 
   * Note: This method should only be called manually, not after command operations.
   * Commands executed through the TranscriptEditorCommandManager already trigger
   * auto-save functionality with appropriate debouncing.
   */
  async saveChanges(): Promise<boolean> {
    try {
      runInAction(() => {
        this.isLoading = true;
        this.error = null;
      });
      
      const success = await this.editor.saveChanges();
      
      runInAction(() => {
        this.isLoading = false;
      });
      
      return success;
    } catch (err) {
      this.handleError('Error saving changes:', err);
      return false;
    }
  }
  
  /**
   * Discard all pending changes
   */
  async discardChanges(): Promise<boolean> {
    try {
      runInAction(() => {
        this.isLoading = true;
        this.error = null;
      });
      
      const success = await this.editor.discardChanges();
      
      runInAction(() => {
        this.isLoading = false;
      });
      
      return success;
    } catch (err) {
      this.handleError('Error discarding changes:', err);
      return false;
    }
  }
  
  /**
   * Undo the last operation
   */
  async undoLastOperation(): Promise<boolean> {
    if (!this.editor) {
      console.warn('Cannot undo: editor not initialized');
      return false;
    }
    
    try {
      // Get the command manager directly from the editor
      const commandManager = this.editor.commandManager;
      
      if (!commandManager) {
        console.warn('Command manager not available');
        return false;
      }
      
      const result = await commandManager.undo();
      
      // Update the UI state after undo
      this.refreshState();
      
      return result;
    } catch (error) {
      this.handleError('Error during undo operation:', error);
      return false;
    }
  }
  
  /**
   * Redo the last undone operation
   */
  async redoLastOperation(): Promise<boolean> {
    if (!this.editor) {
      console.warn('Cannot redo: editor not initialized');
      return false;
    }
    
    try {
      // Get the command manager directly from the editor
      const commandManager = this.editor.commandManager;
      
      if (!commandManager) {
        console.warn('Command manager not available');
        return false;
      }
      
      const result = await commandManager.redo();
      
      // Update the UI state after redo
      this.refreshState();
      
      return result;
    } catch (error) {
      this.handleError('Error during redo operation:', error);
      return false;
    }
  }
  
  /**
   * Check if there are pending changes to be saved
   */
  hasPendingChanges(): boolean {
    if (!this.editor) {
      return false;
    }
    
    return this.editor.hasPendingChanges();
  }
  
  /**
   * Get the size of the undo stack
   */
  getUndoStackSize(): number {
    if (!this.editor) {
      return 0;
    }
    
    const commandManager = this.editor.commandManager;
    if (!commandManager) {
      return 0;
    }
    
    return commandManager.getUndoStackSize();
  }
  
  /**
   * Get the size of the redo stack
   */
  getRedoStackSize(): number {
    if (!this.editor) {
      return 0;
    }
    
    const commandManager = this.editor.commandManager;
    if (!commandManager) {
      return 0;
    }
    
    return commandManager.getRedoStackSize();
  }
  
  /**
   * Gets speaker mappings
   */
  get speakerMappings(): TranscriptVoiceMapping {
    const mappings = this.editor ? this.editor.getSpeakerMappings() : {
      storyId: this.storyId,
      mappings: {},
      lastUpdated: '',
    };

    return mappings as TranscriptVoiceMapping;
  }
  
  /**
   * Loads the album and story speakers
   */
  async loadSpeakers(): Promise<boolean> {
    try {
      // Check for speaker repository
      if (!this.rootStore.datoClientStore || !this.rootStore.datoClientStore.speakerRepository) {
        throw new Error('Speaker repository not available');
      }
      
      const speakerRepo = this.rootStore.datoClientStore.speakerRepository;
      
      // Get the story ID from the editor
      const storyId = this.editor.storyId;
        
      if (!storyId) {
        throw new Error('Story ID not available for loading speakers');
      }
      
      // If speakers are already loaded for this story ID, skip loading them again
      if (this.loadedSpeakersForStoryId === storyId) {
        console.log(`Speakers already loaded for story ${storyId}, skipping reload`);
        return true;
      }
            
      // Load speakers for this story
      try {
        const { albumId, storySpeakers, albumSpeakers } = await speakerRepo.getStorySpeakers(storyId);
        
        runInAction(() => {
          this.albumId = albumId;
          this.albumSpeakers = albumSpeakers;
          this.storySpeakers = storySpeakers;
        });

        
        // Initialize or reinitialize speaker service with the new data
        console.log('Initializing speaker service');
        this.initializeSpeakerService(speakerRepo, storyId, albumId);
        
        // Load speaker mappings  
        await this.editor.loadSpeakerMappings();
        
        // Mark that we've loaded speakers for this story
        this.loadedSpeakersForStoryId = storyId;

        return true;
      } catch (e) {
        throw new Error(`Error getting story speakers: ${e instanceof Error ? e.message : String(e)}`);
      }
    } catch (error) {
      this.handleError('Error loading speakers:', error);
      
      // Set empty values on error
      runInAction(() => {
        this.albumId = '';
        this.albumSpeakers = [];
        this.storySpeakers = [];
      });
      
      // Re-throw to allow proper error handling
      throw error;
    }
  }
  
  /**
   * Create all service instances but don't initialize them
   * This only creates the instances once - they will be reused for the lifetime of the store
   */
  private createServices(): void {
    if (this.servicesCreated) {
      console.log('Services already created, skipping creation');
      return;
    }
    
    console.log('Creating service instances');
    
    // Create SpeakerService if needed
    if (!this.editor.speakerService) {
      console.log('Creating new SpeakerService');
      const speakerService = new SpeakerService();
      this.editor.setSpeakerService(speakerService);
    }
    
    // Create SelectionService if needed
    if (!this.editor.selectionService) {
      console.log('Creating new SelectionService');
      const selectionService = new SelectionService();
      this.editor.setSelectionService(selectionService);
    }
    
    // Create TimelineService if needed
    if (!this.editor.timelineService) {
      console.log('Creating new TimelineService');
      const timelineService = new TimelineService();
      this.editor.setTimelineService(timelineService);
    }
    
    // Create AudioService if needed
    if (!this.editor.audioService) {
      console.log('Creating new AudioService');
      const audioService = new AudioService();
      this.editor.setAudioService(audioService);
    }
    
    this.servicesCreated = true;
  }
  
  /**
   * Initialize or reinitialize just the SpeakerService
   * This can be called multiple times with different parameters
   */
  private initializeSpeakerService(
    speakerRepo: any,
    storyId: string,
    albumId: string
  ): void {
    console.log(`Initializing SpeakerService for storyId ${storyId}`);
    
    // Make sure SpeakerService exists
    if (!this.editor.speakerService) {
      throw new Error('SpeakerService does not exist, createServices must be called first');
    }
    
    // Initialize/reinitialize the SpeakerService
    console.log('Initializing existing SpeakerService');
    if (this.editor.speakerService) {
      this.editor.speakerService.initialize(speakerRepo, storyId, albumId);
    }
  }
  
  /**
   * Handle errors consistently
   */
  private handleError(message: string, error: any): void {
    console.error(message, error);
    
    runInAction(() => {
      this.error = error instanceof Error ? error.message : String(error);
      this.isLoading = false;
    });
  }
  
  /**
   * Gets the number of segments that would be affected by a global speaker change
   */
  getAffectedSegmentsCount(speakerLabel: string): number {
    if (!this.container) return 0;
    
    return this.container.segments.filter(
      segment => segment.speakerLabel === speakerLabel
    ).length;
  }

  addStorySpeaker(speaker: SpeakerData): void {
    this.storySpeakers.push(speaker);
  }

  removeStorySpeaker(speakerId: string): void {
    this.storySpeakers = this.storySpeakers.filter((speaker: SpeakerData) => speaker.id !== speakerId);
  }
  /**
   * Insert text after the selected element
   * @param text Text to insert
   * @returns Promise resolving to true if successful
   */
  async insertTextAfterSelection(text: string): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot insert text: editor or user identity not available');
      return false;
    }

    const selectionService = this.editor.selectionService;
    if (!selectionService || !selectionService.hasSelection) {
      console.warn('Cannot insert text: no selection');
      return false;
    }

    try {
      // Get the last selected element
      const selectedElements = selectionService.getSelectedElements();
      if (selectedElements.length === 0) {
        return false;
      }

      // Use the last element as the insertion point
      const lastElement = selectedElements[selectedElements.length - 1];

      // Create and execute the command
      const { InsertTextCommand } = require('./commands/InsertTextCommand');
      const command = new InsertTextCommand(
        this.editor,
        lastElement,
        text,
        this.currentUserId
      );

      return await this.editor.commandManager.execute(command);
    } catch (error) {
      console.error('Error inserting text after selection:', error);
      return false;
    }
  }

  /**
   * Insert a paragraph break after the selected element
   * @returns Promise resolving to true if successful
   */
  async insertParagraphBreakAfterSelection(): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot insert paragraph break: editor or user identity not available');
      return false;
    }

    const selectionService = this.editor.selectionService;
    if (!selectionService || !selectionService.hasSelection) {
      console.warn('Cannot insert paragraph break: no selection');
      return false;
    }

    try {
      // Get the last selected element
      const selectedElements = selectionService.getSelectedElements();
      if (selectedElements.length === 0) {
        return false;
      }

      // Use the last element as the split point
      const lastElement = selectedElements[selectedElements.length - 1];

      // Create and execute the command
      const { SplitParagraphCommand } = require('./commands/SplitParagraphCommand');
      const command = new SplitParagraphCommand(
        this.editor,
        lastElement.segmentId,
        lastElement.paragraphId,
        lastElement.id,
        this.currentUserId
      );

      return await this.editor.commandManager.execute(command);
    } catch (error) {
      console.error('Error inserting paragraph break:', error);
      return false;
    }
  }

  /**
   * Delete the selected text elements
   * @returns Promise resolving to true if successful
   */
  async deleteSelectedElements(): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot delete selected elements: editor or user identity not available');
      return false;
    }

    const selectionService = this.editor.selectionService;
    if (!selectionService || !selectionService.hasSelection) {
      console.warn('Cannot delete elements: no selection');
      return false;
    }

    try {
      // Get the selected elements
      const selectedElements = selectionService.getSelectedElements();
      if (selectedElements.length === 0) {
        return false;
      }

      // Start batch operation
      this.editor.beginBatch();

      // Delete each selected element
      let success = true;
      for (const element of selectedElements) {
        const result = this.editor.deleteElement(
          element.segmentId,
          element.paragraphId,
          element.id,
          this.currentUserId
        );
        if (!result) {
          success = false;
        }
      }

      // End batch operation
      this.editor.endBatch();

      // Clear the selection
      selectionService.clearAllSelections();

      return success;
    } catch (error) {
      console.error('Error deleting selected elements:', error);
      this.editor.endBatch(); // Ensure batch operation is ended even on error
      return false;
    }
  }

  /**
   * Assign a color to the selected elements
   * @param color The color to assign (hex format)
   * @returns Promise resolving to true if successful
   */
  async assignColorToSelectedElements(color: string): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot assign color: editor or user identity not available');
      return false;
    }

    const selectedElements = this.editor.getSelectionService()?.getSelectedElements();
    if (!selectedElements || !selectedElements.length) {
      console.warn('No elements selected for color assignment');
      return false;
    }

    const elementIds = selectedElements.map(e => e.id);
    return await this.editor.assignColorToElements(elementIds, color, this.currentUserId);
  }

  /**
   * Clear color from the selected elements
   * @returns Promise resolving to true if successful
   */
  async clearColorFromSelectedElements(): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot clear color: editor or user identity not available');
      return false;
    }

    const selectedElements = this.editor.getSelectionService()?.getSelectedElements();
    if (!selectedElements || !selectedElements.length) {
      console.warn('No elements selected for color clearing');
      return false;
    }

    const elementIds = selectedElements.map(e => e.id);
    return await this.editor.clearColorFromElements(elementIds, this.currentUserId);
  }

  /**
   * Get all colors currently used in the transcript
   * @returns Array of color strings (hex format)
   */
  getUsedColors(): string[] {
    if (!this.editor) {
      return [];
    }

    return this.editor.getUsedColors();
  }

  /**
   * Save the current text selection as a clip in creator studio
   * @param title Optional title for the clip
   * @returns Promise resolving to true if successful, includes the new video ID
   */
  async saveSelectionAsClip(title?: string, videoCreatorStore?: VideoCreatorStore): Promise<{ success: boolean; videoId?: string; error?: string }> {
    try {
      // Check if we have a selection
      const selectionService = this.editor?.getSelectionService();
      if (!selectionService || !selectionService.hasSelection) {
        return { success: false, error: 'No text selected' };
      }

      // Get selected elements
      const selectedElements = selectionService.getSelectedElements();
      if (selectedElements.length === 0) {
        return { success: false, error: 'No elements in selection' };
      }

      // Get the container to find element positions
      const container = this.editor.getContainer();
      if (!container) {
        return { success: false, error: 'No transcript container available' };
      }

      // Find the start and end indices of the selection
      let startIndex = -1;
      let endIndex = -1;
      let elementCount = 0;

      // Iterate through all elements to find indices
      outerLoop: for (const segment of container.segments) {
        for (const paragraph of segment.paragraphs) {
          for (const element of paragraph.elements) {
            if (element.id === selectedElements[0].id) {
              startIndex = elementCount;
            }
            if (element.id === selectedElements[selectedElements.length - 1].id) {
              endIndex = elementCount;
              break outerLoop;
            }
            elementCount++;
          }
        }
      }

      if (startIndex === -1 || endIndex === -1) {
        return { success: false, error: 'Could not determine selection boundaries' };
      }

      // Get timing information from text elements
      const firstTextElement = selectedElements.find(e => e.element.type === 'text');
      const lastTextElement = [...selectedElements].reverse().find(e => e.element.type === 'text');

      const startTime = (firstTextElement?.element as any)?.startTime || 0;
      const endTime = (lastTextElement?.element as any)?.endTime || 0;
      const duration = endTime - startTime;

      // Get the selected text
      const selectedText = selectedElements
        .map(e => e.element.value)
        .join('')
        .trim();

      // Get speaker information from the segment containing the first element
      const firstSegmentId = selectedElements[0].segmentId;
      const segment = container.getSegment(firstSegmentId);
      const speakerLabel = segment?.speakerLabel || '';
      
      let speakerName = 'Unknown Speaker';
      if (speakerLabel && this.editor.speakerService) {
        speakerName = this.editor.speakerService.getSpeakerName(speakerLabel) || 'Unknown Speaker';
      }

      // Generate a title if not provided
      const clipTitle = title || `${speakerName} - Video Clip`;

      // Access the video creator store from root store
      if (!videoCreatorStore || !videoCreatorStore.currentVideo) {
        return { success: false, error: 'Video creator not available' };
      }

      // Get the original video and source
      const originalVideo = toJS(videoCreatorStore.currentVideo);
      
      let src = ''
      
      if (videoCreatorStore.story?.originalVideo) {
        src = videoCreatorStore.story?.originalVideo?.video?.mp4UrlLow ||
              videoCreatorStore.story?.originalVideo?.video?.mp4UrlMedium ||
              videoCreatorStore.story?.originalVideo?.url;
      }

      // // Create clip data structure similar to AIClipProducer
      const clip = {
        title: clipTitle,
        text: selectedText,
        duration: duration,
        startTime: startTime,
        theme: 'selection', // Custom theme for manual selections
        transcriptPosition: {
          startIndex: startIndex,
          endIndex: endIndex
        },
        speakerName: speakerName,
        speakerLabel: speakerLabel,
        platform: 'creator-studio',
        src: src,
      };

      // Create a new video from the selection
      await videoCreatorStore.createNewVideoForContentClip(
        originalVideo,
        clip
      );

      videoCreatorStore.currentVideo!.clipJson = {
        startTime: clip.startTime,
        duration: clip.duration,
      };

      videoCreatorStore.currentVideo!.transcriptionText = selectedText;

      // Save the video
      await videoCreatorStore.saveCurrentAndParentVideos(false, false, false);

      // Get the new video ID
      const newVideoId = videoCreatorStore.currentVideo?.id;

      if (newVideoId) {
        return { 
          success: true, 
          videoId: newVideoId 
        };
      } else {
        return { 
          success: false, 
          error: 'Failed to get video ID after save' 
        };
      }

    } catch (error) {
      console.error('Error saving selection as clip:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }


  /**
   * Format the transcript with configurable options for speakers and timecodes
   * @param withSpeakers Include speaker labels in the output
   * @param withTimecodes Include timecodes in the output
   * @returns Formatted transcript string
   */
  formatTranscript(withSpeakers: boolean = true, withTimecodes: boolean = false): string {
    if (!this.container) return "";

    let speakerMap: Record<string, string> = {};

    // Only build the speaker map if we're including speakers
    if (withSpeakers) {
      const speakers = this.editor.speakerService?.getAllSpeakers(this.container, this);
      if (!speakers) return "";

      for (const speaker of speakers.mapped) {
        if (speaker.name && speaker.title) {
          speakerMap[speaker.speakerLabel] = speaker.name + ' (' + speaker.title + ')';
        } else {
          speakerMap[speaker.speakerLabel] = speaker.name || this.editor.speakerService?.getSpeakerName(speaker.speakerLabel) || speaker.speakerLabel;
        }
      }
      for (const speaker of speakers.unmapped) {
        speakerMap[speaker.speakerLabel] = this.editor.speakerService?.getSpeakerName(speaker.speakerLabel) || speaker.speakerLabel;
      }
    }

    let formattedText = '';

    this.container?.segments.forEach(segment => {
      // Format segment header with speaker and/or timecodes
      if (withSpeakers && withTimecodes) {
        // Format timecode as [HH:MM:SS]
        const startTimeFormatted = this.formatTimecode(segment.startTime);
        formattedText += `${speakerMap[segment.speakerLabel]} [${startTimeFormatted}]:\n`;
      } else if (withSpeakers) {
        formattedText += `${speakerMap[segment.speakerLabel]}:\n`;
      }

      // Format paragraphs
      segment.paragraphs.forEach(paragraph => {
        if (withTimecodes && !withSpeakers) {
          // For paragraphs with just timecodes, show time before each paragraph
          const paragraphTimeFormatted = this.formatTimecode(paragraph.startTime);
          formattedText += `[${paragraphTimeFormatted}] ${paragraph.getText()}\n\n`;
        } else {
          // Otherwise just show the text
          formattedText += `${paragraph.getText()}\n\n`;
        }
      });

      formattedText += '\n\n';
    });

    return formattedText;
  }

  /**
   * Format a time in seconds to HH:MM:SS format
   * @param timeInSeconds Time in seconds
   * @returns Formatted timecode string
   */
  private formatTimecode(timeInSeconds: number): string {
    if (timeInSeconds === undefined || isNaN(timeInSeconds)) {
      return "00:00:00";
    }

    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Combines selected paragraphs
   * Requires that all selected elements belong to paragraphs in the same segment
   * @returns Promise resolving to true if successful
   */
  async combineSelectedParagraphs(): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot combine paragraphs: editor or user identity not available');
      return false;
    }

    const selectionService = this.editor.selectionService;
    if (!selectionService || !selectionService.hasSelection) {
      console.warn('Cannot combine paragraphs: no selection');
      return false;
    }

    try {
      // Get the selected elements
      const selectedElements = selectionService.getSelectedElements();
      if (selectedElements.length === 0) {
        console.warn('Cannot combine paragraphs: no elements selected');
        return false;
      }

      // Group elements by paragraph
      const paragraphMap = new Map<string, {
        id: string,
        segmentId: string,
        elements: Array<{ id: string, segmentId: string, paragraphId: string }>,
        index?: number
      }>();

      // Make sure all elements are in the same segment
      let segmentId = selectedElements[0].segmentId;
      
      for (const element of selectedElements) {
        // Check if all elements are in the same segment
        if (element.segmentId !== segmentId) {
          console.warn('Cannot combine paragraphs: elements span multiple segments');
          return false;
        }

        // Group by paragraph
        if (!paragraphMap.has(element.paragraphId)) {
          paragraphMap.set(element.paragraphId, {
            id: element.paragraphId,
            segmentId: element.segmentId,
            elements: []
          });
        }
        paragraphMap.get(element.paragraphId)!.elements.push(element);
      }

      // Need at least 2 paragraphs to combine
      if (paragraphMap.size < 2) {
        console.warn('Cannot combine paragraphs: at least 2 paragraphs must be selected');
        return false;
      }

      // Get the segment to determine paragraph order
      const container = this.editor.getContainer();
      if (!container) {
        console.warn('Cannot combine paragraphs: no container');
        return false;
      }

      const segment = container.getSegment(segmentId);
      if (!segment) {
        console.warn('Cannot combine paragraphs: segment not found');
        return false;
      }

      // Add paragraph index information
      for (const paragraph of paragraphMap.values()) {
        const index = segment.paragraphs.findIndex(p => p.id === paragraph.id);
        if (index === -1) {
          console.warn(`Cannot combine paragraphs: paragraph ${paragraph.id} not found in segment`);
          return false;
        }
        paragraph.index = index;
      }

      // Sort paragraphs by index
      const sortedParagraphs = Array.from(paragraphMap.values())
        .sort((a, b) => (a.index || 0) - (b.index || 0));

      // Start batch operation
      this.editor.beginBatch();

      // Combine paragraphs one by one
      let success = true;
      for (let i = 1; i < sortedParagraphs.length; i++) {
        const { MergeParagraphsCommand } = require('./commands/MergeParagraphsCommand');
        const command = new MergeParagraphsCommand(
          this.editor,
          segmentId,
          sortedParagraphs[0].id, // Always merge into the first paragraph
          sortedParagraphs[i].id,
          this.currentUserId
        );
        
        const result = await this.editor.commandManager.execute(command);
        if (!result) {
          success = false;
          break;
        }
      }

      // End batch operation
      this.editor.endBatch();

      // Clear the selection
      selectionService.clearAllSelections();

      return success;
    } catch (error) {
      console.error('Error combining paragraphs:', error);
      this.editor.endBatch(); // Ensure batch operation is ended even on error
      return false;
    }
  }

  /**
   * Combines selected segments
   * Requires that all selected elements belong to segments
   * If segments have different speakers, all elements will use the speaker from the first segment
   * @returns Promise resolving to true if successful
   */
  async combineSelectedSegments(): Promise<boolean> {
    if (!this.editor || !this.currentUserId) {
      console.warn('Cannot combine segments: editor or user identity not available');
      return false;
    }

    const selectionService = this.editor.selectionService;
    if (!selectionService || !selectionService.hasSelection) {
      console.warn('Cannot combine segments: no selection');
      return false;
    }

    try {
      // Get the selected elements
      const selectedElements = selectionService.getSelectedElements();
      if (selectedElements.length === 0) {
        console.warn('Cannot combine segments: no elements selected');
        return false;
      }

      // Group elements by segment
      const segmentMap = new Map<string, {
        id: string,
        elements: Array<{ id: string, segmentId: string, paragraphId: string }>,
        speaker?: string,
        index?: number
      }>();

      for (const element of selectedElements) {
        if (!segmentMap.has(element.segmentId)) {
          segmentMap.set(element.segmentId, {
            id: element.segmentId,
            elements: []
          });
        }
        segmentMap.get(element.segmentId)!.elements.push(element);
      }

      // Need at least 2 segments to combine
      if (segmentMap.size < 2) {
        console.warn('Cannot combine segments: at least 2 segments must be selected');
        return false;
      }

      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        console.warn('Cannot combine segments: no container');
        return false;
      }

      // Add segment index and speaker information
      for (const segment of segmentMap.values()) {
        const segmentObj = container.getSegment(segment.id);
        if (!segmentObj) {
          console.warn(`Cannot combine segments: segment ${segment.id} not found`);
          return false;
        }
        segment.index = container.segments.findIndex(s => s.id === segment.id);
        segment.speaker = segmentObj.speakerLabel;
      }

      // Sort segments by index
      const sortedSegments = Array.from(segmentMap.values())
        .sort((a, b) => (a.index || 0) - (b.index || 0));

      // Start batch operation
      this.editor.beginBatch();

      // Get the speaker label from the first segment
      const targetSpeakerLabel = sortedSegments[0].speaker;

      // If the segments have different speakers, first assign the target speaker to all elements
      const differentSpeakers = sortedSegments.some(s => s.speaker !== targetSpeakerLabel);
      
      if (differentSpeakers && targetSpeakerLabel) {
        // First, move all paragraphs to the first segment
        for (let i = 1; i < sortedSegments.length; i++) {
          const segmentObj = container.getSegment(sortedSegments[i].id);
          if (!segmentObj) continue;
          
          // Move each paragraph to the first segment
          for (const paragraph of segmentObj.paragraphs) {
            const { MoveParagraphToSegmentCommand } = require('./commands/MoveParagraphToSegmentCommand');
            const command = new MoveParagraphToSegmentCommand(
              this.editor,
              segmentObj.id,
              paragraph.id,
              sortedSegments[0].id,
              this.currentUserId
            );
            
            await this.editor.commandManager.execute(command);
          }
        }
        
        // Now assign the speaker to all elements in the first segment
        const firstSegmentObj = container.getSegment(sortedSegments[0].id);
        if (firstSegmentObj) {
          firstSegmentObj.speakerLabel = targetSpeakerLabel;
          
          // Notify about the speaker change
          this.editor.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
            segment: firstSegmentObj,
            speakerLabel: targetSpeakerLabel
          });
        }
      } else {
        // If speakers are the same, we can use the built-in segment merging functionality
        
        // Get segment objects
        const segmentObjects = sortedSegments.map(s => container.getSegment(s.id))
          .filter(s => s !== null) as any[];
        
        if (segmentObjects.length >= 2) {
          // Use the private method to merge segments
          // Since it's private, we need to use Function.prototype.call to access it
          const mergeMethod = (this.editor as any).mergeSegments;
          if (typeof mergeMethod === 'function') {
            const mergedSegment = mergeMethod.call(this.editor, segmentObjects);
            
            // Remove the merged segments except the first one
            for (let i = 1; i < segmentObjects.length; i++) {
              container.removeSegment(segmentObjects[i].id);
            }
            
            // Notify about the merge
            this.editor.notifyObservers(TranscriptEditorChangeType.SEGMENTS_MERGED, {
              mergedSegment,
              mergedSegmentIds: segmentObjects.slice(1).map(s => s.id)
            });
          }
        }
      }

      // End batch operation
      this.editor.endBatch();

      // Clear the selection
      selectionService.clearAllSelections();

      return true;
    } catch (error) {
      console.error('Error combining segments:', error);
      this.editor.endBatch(); // Ensure batch operation is ended even on error
      return false;
    }
  }
}